import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PetsIcon from "@mui/icons-material/Pets";
import { motion, AnimatePresence } from "framer-motion";

import type { Dog, DogFormData } from "../types";
import { BasicsStep } from "../components/DogEditor/BasicsStep";
import { PhysicalStep } from "../components/DogEditor/PhysicalStep";
import { DietaryStep } from "../components/DogEditor/DietaryStep";
import { QUIZ } from "@/theme/quizTokens";

const COLORS = {
  bg: "#FFF7F3",
  panel: QUIZ.color.white,
  ink: QUIZ.color.brown,
  inkSoft: "rgba(74,48,38,0.75)",
  border: "rgba(74,48,38,0.12)",
  borderStrong: "rgba(74,48,38,0.22)",
  accent: QUIZ.color.brown,
  accentSoft: QUIZ.color.cream2,
  primary: QUIZ.color.brown,
  primaryHover: QUIZ.color.ink,
  disabledBg: "#D0D8E0",
  disabledInk: "#8A9AAA",
};

const STEPS = [
  { key: "basics", label: "Basics", emoji: "🐕" },
  { key: "physical", label: "Physical", emoji: "💪" },
  { key: "dietary", label: "Health", emoji: "🥗" },
] as const;

export function DogEditPage({
  dog,
  onBack,
  onSave,
}: {
  dog: Dog | undefined;
  onBack: () => void;
  onSave: (formData: DogFormData) => Promise<void>;
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<Partial<DogFormData>>({});
  const [initialForm, setInitialForm] = useState<Partial<DogFormData>>({});
  const [saving, setSaving] = useState(false);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [shake, setShake] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  // Initialize form when dog changes
  useEffect(() => {
    if (!dog) return;
    const initial: Partial<DogFormData> = {
      name: dog.name || "",
      breed: dog.breed || "",
      sex: dog.sex,
      reproduction: dog.reproduction,
      ageYears: dog.ageYears ?? 1,
      ageMonths: dog.ageMonths ?? 0,
      weightKg: dog.weightKg ?? 10,
      bodyCondition: dog.bodyCondition,
      activityLevel: dog.activityLevel,
      allergies: dog.allergies ?? [],
      healthIssues: dog.healthIssues ?? [],
      treatFrequency: dog.treatFrequency,
    };
    setForm(initial);
    setInitialForm(initial);
    setActiveStep(0);
    setStepErrors({});
  }, [dog]);

  // Dirty detection
  const isDirty = useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  const validateStep = useCallback(
    (step: number): Record<string, string> => {
      const errors: Record<string, string> = {};
      if (step === 0) {
        if (!form.name || !String(form.name).trim()) errors.name = "Name is required";
        if (!form.breed || !String(form.breed).trim()) errors.breed = "Breed is required";
      } else if (step === 1) {
        if (!form.weightKg || Number(form.weightKg) <= 0) errors.weightKg = "Weight is required";
        if (!form.bodyCondition) errors.bodyCondition = "Please select a body condition";
        if (!form.activityLevel) errors.activityLevel = "Please select an activity level";
      }
      return errors;
    },
    [form]
  );

  const canSave = useMemo(() => {
    const nameOk = Boolean(form.name && String(form.name).trim().length > 0);
    const breedOk = Boolean(form.breed && String(form.breed).trim().length > 0);
    const weightOk = Boolean(form.weightKg && Number(form.weightKg) > 0);
    return nameOk && breedOk && weightOk;
  }, [form]);

  const isLastStep = activeStep === STEPS.length - 1;

  const updateForm = (updates: Partial<DogFormData>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const handleBack = () => {
    setStepErrors({});
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleNavigateBack = () => {
    if (isDirty) {
      setDiscardDialogOpen(true);
    } else {
      onBack();
    }
  };

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave(form as DogFormData);
    } catch (error) {
      console.error("Failed to save dog:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    const errors = validateStep(activeStep);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setStepErrors({});
    if (isLastStep) handleSave();
    else setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const stepTitle = useMemo(() => {
    const name = dog?.name || "your dog";
    switch (activeStep) {
      case 0: return `Basics about ${name}`;
      case 1: return `Physical details`;
      case 2: return `Health & diet`;
      default: return `Edit ${name}`;
    }
  }, [activeStep, dog?.name]);

  const stepSubtitle = useMemo(() => {
    switch (activeStep) {
      case 0: return "Name and breed help us personalize the plan";
      case 1: return "Weight, activity and condition affect calories";
      case 2: return "Allergies and health info keep meals safe";
      default: return "Update your dog's profile";
    }
  }, [activeStep]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return <BasicsStep form={form} onChange={updateForm} errors={stepErrors} />;
      case 1: return <PhysicalStep form={form} onChange={updateForm} errors={stepErrors} />;
      case 2: return <DietaryStep form={form} onChange={updateForm} />;
      default: return null;
    }
  };

  if (!dog) {
    return (
      <Box sx={{ maxWidth: 680, mx: "auto" }}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={onBack}
          sx={{ mb: 2, textTransform: "none", fontWeight: 700, color: QUIZ.color.brown }}
        >
          Back
        </Button>
        <Typography sx={{ textAlign: "center", py: 4, color: QUIZ.color.brown, opacity: 0.6 }}>
          Dog not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 680, mx: "auto" }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={handleNavigateBack}
        sx={{
          mb: 2,
          textTransform: "none",
          fontWeight: 700,
          color: QUIZ.color.brown,
          "&:hover": { bgcolor: "rgba(255,225,221,0.3)" },
        }}
      >
        Back to Profile
      </Button>

      {/* Wizard card */}
      <Box
        sx={{
          borderRadius: "18px",
          bgcolor: COLORS.panel,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box sx={{ flexShrink: 0, borderBottom: `1px solid ${COLORS.border}`, background: COLORS.bg, p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: COLORS.accentSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <PetsIcon sx={{ color: COLORS.accent }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: COLORS.ink,
                  fontWeight: 900,
                  fontSize: { xs: 18, sm: 20, md: 22 },
                  fontFamily: QUIZ.font.display,
                  lineHeight: 1.15,
                }}
              >
                Edit {dog.name ? `${dog.name}'s` : "dog"} profile
              </Typography>
              <Typography sx={{ mt: 0.5, color: COLORS.inkSoft, fontSize: 13, fontWeight: 600 }}>
                Keep their details accurate for the best plan
              </Typography>
            </Box>
          </Box>

          {/* Step chips */}
          <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 1.5 }}>
            {STEPS.map((step, idx) => {
              const isActive = idx === activeStep;
              const isCompleted = idx < activeStep;
              return (
                <Box
                  key={step.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setStepErrors({}); setActiveStep(idx); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { setStepErrors({}); setActiveStep(idx); }
                  }}
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 1.25,
                    borderRadius: "12px",
                    cursor: "pointer",
                    userSelect: "none",
                    border: `1px solid ${isActive ? "rgba(74, 48, 38,0.35)" : COLORS.border}`,
                    bgcolor: isActive
                      ? "rgba(74, 48, 38,0.16)"
                      : isCompleted
                        ? "rgba(46,125,50,0.10)"
                        : QUIZ.color.white,
                    transition: "all 0.18s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: isActive ? "rgba(74, 48, 38,0.45)" : COLORS.borderStrong,
                      bgcolor: isActive ? "rgba(74, 48, 38,0.18)" : "rgba(255,225,221,0.35)",
                    },
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
                  ) : (
                    <Typography sx={{ fontSize: 14 }}>{step.emoji}</Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: { xs: 11, sm: 13 },
                      fontWeight: 900,
                      color: isActive ? COLORS.accent : isCompleted ? "#2E7D32" : COLORS.inkSoft,
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <LinearProgress
            variant="determinate"
            value={((activeStep + 1) / STEPS.length) * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: "rgba(74,48,38,0.08)",
              "& .MuiLinearProgress-bar": {
                bgcolor: COLORS.primary,
                borderRadius: 2,
                transition: "transform 0.3s ease",
              },
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, bgcolor: QUIZ.color.warmWhite }}>
          <Box
            sx={{
              maxWidth: 600,
              mx: "auto",
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 2.5 },
              pb: { xs: 4, sm: 2 },
            }}
          >
            <Typography
              sx={{
                color: COLORS.ink,
                fontWeight: 900,
                fontSize: { xs: 18, sm: 20 },
                fontFamily: QUIZ.font.display,
                textAlign: "center",
                mb: 0.5,
              }}
            >
              {stepTitle}
            </Typography>
            <Typography
              sx={{ color: COLORS.inkSoft, fontSize: 14, fontWeight: 600, mb: 2, textAlign: "center" }}
            >
              {stepSubtitle}
            </Typography>

            <AnimatePresence mode="wait">
              <motion.div
                key={STEPS[activeStep]?.key ?? String(activeStep)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <Box
                  sx={{
                    backgroundColor: COLORS.bg,
                    border: `1px solid ${Object.keys(stepErrors).length > 0 ? "rgba(211,47,47,0.4)" : COLORS.border}`,
                    borderRadius: "16px",
                    p: { xs: 2, sm: 2.5 },
                    animation: shake ? "shake 0.4s ease-in-out" : "none",
                    "@keyframes shake": {
                      "0%, 100%": { transform: "translateX(0)" },
                      "20%": { transform: "translateX(-6px)" },
                      "40%": { transform: "translateX(6px)" },
                      "60%": { transform: "translateX(-4px)" },
                      "80%": { transform: "translateX(4px)" },
                    },
                  }}
                >
                  {renderStepContent()}
                </Box>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>

        {/* Sticky footer */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            bgcolor: QUIZ.color.warmWhite,
            borderTop: `1px solid ${COLORS.border}`,
            flexShrink: 0,
            display: "flex",
            gap: 1.5,
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          {activeStep > 0 ? (
            <Button
              onClick={handleBack}
              disabled={saving}
              sx={{
                flex: 1,
                py: 1.45,
                minHeight: { xs: 48, sm: "auto" },
                borderRadius: "12px",
                border: `2px solid ${COLORS.accent}`,
                color: COLORS.accent,
                fontWeight: 900,
                fontSize: 14,
                textTransform: "none",
                bgcolor: QUIZ.color.warmWhite,
                "&:hover": { bgcolor: "rgba(255,225,221,0.35)" },
                "&:disabled": { opacity: 0.6, borderColor: COLORS.disabledBg, color: COLORS.disabledInk },
              }}
            >
              Back
            </Button>
          ) : (
            <Button
              onClick={handleNavigateBack}
              disabled={saving}
              sx={{
                flex: 1,
                py: 1.45,
                minHeight: { xs: 48, sm: "auto" },
                borderRadius: "12px",
                border: `2px solid ${COLORS.borderStrong}`,
                color: COLORS.inkSoft,
                fontWeight: 900,
                fontSize: 14,
                textTransform: "none",
                bgcolor: QUIZ.color.warmWhite,
                "&:hover": { bgcolor: "rgba(74,48,38,0.05)" },
                "&:disabled": { opacity: 0.6 },
              }}
            >
              Cancel
            </Button>
          )}

          <Button
            onClick={handleNext}
            disabled={(isLastStep && !canSave) || saving}
            sx={{
              flex: 1,
              py: 1.45,
              minHeight: { xs: 48, sm: "auto" },
              borderRadius: "12px",
              bgcolor: COLORS.primary,
              color: QUIZ.color.white,
              fontWeight: 900,
              fontSize: 14,
              textTransform: "none",
              boxShadow: "0 10px 24px rgba(74, 48, 38,0.28)",
              "&:hover": { bgcolor: COLORS.primaryHover },
              "&:disabled": { bgcolor: COLORS.disabledBg, color: COLORS.disabledInk, boxShadow: "none" },
            }}
          >
            {saving ? "Saving..." : isLastStep ? "Save Changes" : "Continue"}
          </Button>
        </Box>
      </Box>

      {/* Discard changes dialog */}
      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 800, color: QUIZ.color.brown }}>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: QUIZ.color.brown, opacity: 0.8 }}>
            You have unsaved changes. Are you sure you want to leave?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDiscardDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: QUIZ.color.brown }}
          >
            Keep Editing
          </Button>
          <Button
            onClick={() => { setDiscardDialogOpen(false); onBack(); }}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "#D14C3F",
              "&:hover": { bgcolor: "#B7372B" },
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
